// Live product catalog for the menu maker.
//
// Flavors come from the public Shopify storefront feed
// (https://wonderfuldental.com/products.json), which answers with
// Access-Control-Allow-Origin: *, so the browser can read it directly.
// A copy is cached in localStorage, and a bundled snapshot
// (src/data/catalog-snapshot.json, refreshed with `npm run snapshot`)
// is used when neither the network nor the cache is available.
//
// Flavors that are still draft products in Shopify (Flow, until launch) come
// from src/data/coming-soon.json with a Coming Soon flag. The flag goes away
// by itself as soon as products.json lists the product.

import snapshot from '../data/catalog-snapshot.json';
import comingSoonConfig from '../data/coming-soon.json';

export const STORE_ORIGIN = 'https://wonderfuldental.com';
export const PRODUCTS_URL = `${STORE_ORIGIN}/products.json`;
const PAGE_LIMIT = 250;
const MAX_PAGES = 10;
const FETCH_TIMEOUT_MS = 8000;

// v3 caches the storefront products themselves (only the fields the parser
// reads), not a parsed catalog: every load parses them with this build's
// rules and this build's coming-soon.json, so a deploy that changes either
// takes effect at once instead of after the cache expires. v1 and v2 held
// parsed catalogs and are removed.
export const CACHE_KEY = 'wd-menu-maker:catalog:v3';
const OLD_CACHE_KEYS = ['wd-menu-maker:catalog:v1', 'wd-menu-maker:catalog:v2'];
export const CACHE_TTL_MS = 6 * 60 * 60 * 1000;

export type ProductLineId =
  | 'prophy-paste'
  | 'adult-prophy-paste'
  | 'varnish-sticks'
  | 'varnish-cups'
  | 'flow';

export interface ProductLine {
  id: ProductLineId;
  /** Product name as sold in Shopify. */
  name: string;
  /** Default banner text on the printed menu (patients read this). */
  menuLabel: string;
}

// Order here is the order offices see in the product picker.
export const PRODUCT_LINES: ProductLine[] = [
  { id: 'prophy-paste', name: 'Prophy Paste', menuLabel: 'Prophy Paste' },
  { id: 'adult-prophy-paste', name: 'Adult Prophy Paste', menuLabel: 'Adult Prophy Paste' },
  { id: 'varnish-sticks', name: 'Fluoride Varnish Sticks', menuLabel: 'Fluoride Varnish' },
  { id: 'varnish-cups', name: 'Fluoride Varnish Cups', menuLabel: 'Fluoride Varnish' },
  { id: 'flow', name: 'Flow', menuLabel: 'Flow' },
];

export const getProductLine = (id: ProductLineId): ProductLine =>
  PRODUCT_LINES.find(line => line.id === id) || PRODUCT_LINES[0];

export interface CatalogFlavor {
  /** Stable id: `${line}:${slug}`. */
  id: string;
  line: ProductLineId;
  name: string;
  slug: string;
  color?: string;
  image?: string;
  productUrl?: string;
  /** Shopify product handle, when the flavor is one product. */
  handle?: string;
  /** In Shopify as a draft product only: offices see a Coming Soon tag. */
  comingSoon?: boolean;
}

export type Catalog = Record<ProductLineId, CatalogFlavor[]>;

export type CatalogSource = 'live' | 'cache' | 'snapshot';

/** The subset of a Shopify products.json product that the parser reads. */
export interface ShopifyProduct {
  title: string;
  handle?: string;
  product_type?: string;
  tags?: string[] | string;
  images?: { src: string }[];
  options?: { name: string; values?: string[] }[];
  variants?: { option1?: string | null; option2?: string | null; option3?: string | null; featured_image?: { src: string } | null }[];
}

// Flavor art bundled in public/images, keyed by flavor slug.
const MASCOTS: Record<string, string> = {
  bluerazz: 'bluerazz.png',
  bubblegum: 'bubblegum.png',
  cherry: 'cherry.png',
  chocolate: 'chocolate.png',
  cinnamon: 'cinnamon.png',
  cottoncandy: 'flow-cottoncandy.webp',
  marshmallow: 'marshmallow.png',
  mint: 'mint.png',
  plain: 'plain.png',
  plainpumice: 'plain.png',
  smores: 'smores.png',
  strawberry: 'strawberry.png',
  variety: 'variety.png',
};

// Lines with their own cast: Flow's flavors are drawn differently from the
// classic mascots. A Flow flavor without Flow art falls back to MASCOTS.
const LINE_ART: Partial<Record<ProductLineId, Record<string, string>>> = {
  flow: {
    bluerazz: 'flow-bluerazz.webp',
    cottoncandy: 'flow-cottoncandy.webp',
    marshmallow: 'flow-marshmallow.webp',
    mint: 'flow-mint.webp',
  },
};

/** Bundled art for a flavor, or undefined when there is none yet. */
export const flavorArt = (line: ProductLineId, slug: string, tagSlug = ''): string | undefined => {
  const file = LINE_ART[line]?.[slug] || MASCOTS[slug] || (tagSlug ? MASCOTS[tagSlug] : undefined);
  return file ? `/images/${file}` : undefined;
};

// Samplers, kits and bundles are not a flavor a patient can pick. Variety is
// listed: it is a product offices buy (Variety Prophy Paste).
const ASSORTMENT = /\b(sampler|assorted|assortment|samples?|kits?|bundles?)\b/i;

// Product types that are never a flavor, even when branded Flow
// (for example a future "Flow Applicator Brushes").
const NON_FLAVOR_TYPES = /^(angles?|brush(es)?|accessory|accessories|applicators?)$/;

// A tag marks a Flow product only when the tag itself starts with the word
// Flow ("flow", "Flow Varnish", "flow_varnish"). A cross-sell tag on another
// product, such as "pairs_with:flow", must not move it into the Flow line.
const FLOW_TAG = /^flow(\b|_)/i;

export const collapseSpaces = (value: string) => value.replace(/\s+/g, ' ').trim();

export const slugify = (value: string) =>
  value.toLowerCase().replace(/^adult\s+/, '').replace(/[^a-z0-9]/g, '');

const titleCase = (value: string) =>
  collapseSpaces(value)
    .toLowerCase()
    .replace(/(^|[\s-])([a-z])/g, (_m, sep: string, ch: string) => sep + ch.toUpperCase());

/** "variety pack" -> "Variety", "blue razz" -> "Blue Razz". */
const cleanFlavorName = (value: string) => titleCase(collapseSpaces(value).replace(/\s+packs?$/i, ''));

const tagList = (tags: ShopifyProduct['tags']): string[] =>
  Array.isArray(tags) ? tags : typeof tags === 'string' ? tags.split(',').map(t => t.trim()) : [];

export const classifyProduct = (product: ShopifyProduct): ProductLineId | null => {
  const title = collapseSpaces(product.title || '');
  const type = (product.product_type || '').toLowerCase().trim();
  const tags = tagList(product.tags);

  if (!title || ASSORTMENT.test(title) || NON_FLAVOR_TYPES.test(type)) return null;
  if (/\bflow\b/i.test(title) || /\bflow\b/i.test(type) || tags.some(tag => FLOW_TAG.test(tag.trim()))) return 'flow';
  if (type === 'paste' || /\b(prophy|pumice)\s+paste\b/i.test(title)) {
    return /^adult\b/i.test(title) ? 'adult-prophy-paste' : 'prophy-paste';
  }
  if (type === 'fluoride varnish' || /\bvarnish\b/i.test(title)) {
    return /\bcups?\b/i.test(title) ? 'varnish-cups' : 'varnish-sticks';
  }
  return null;
};

/** Flavor name from a product title, e.g. "Adult Mint  Prophy Paste" -> "Mint". */
// Words that name the product line rather than the flavor.
const LINE_WORDS = /\b(adult|flow|fluoride|varnish|prophy|paste|cups?|sticks?)\b/gi;

/**
 * Flavor name from a product title: "Adult Mint  Prophy Paste" -> "Mint",
 * "Mint Fluoride Varnish Cups w/Brushes" -> "Mint", "Plain Pumice Paste" ->
 * "Plain Pumice", "Flow Fluoride Varnish - Cotton Candy" -> "Cotton Candy".
 */
export const flavorNameFromTitle = (title: string): string => {
  let name = collapseSpaces(title);
  name = name.replace(/\s*(w\/|with)\s*brushes\b.*$/i, '');
  name = name.replace(LINE_WORDS, ' ');
  name = name.replace(/[–—:|]+|\s-+\s|^\s*-+|-+\s*$/g, ' ');
  name = collapseSpaces(name);
  return name ? cleanFlavorName(name) : '';
};

/** Values of a "Flavor" option, when one product sells several flavors as variants. */
const flavorOptionValues = (product: ShopifyProduct): { value: string; image?: string }[] => {
  const options = product.options || [];
  const index = options.findIndex(option => /^flavou?rs?$/i.test((option.name || '').trim()));
  if (index < 0) return [];
  const key = (['option1', 'option2', 'option3'] as const)[index];
  const values = (options[index].values || [])
    .map(collapseSpaces)
    .filter(value => value && !ASSORTMENT.test(value));
  return values.map(value => {
    const variant = (product.variants || []).find(v => v && key && collapseSpaces(v[key] || '') === value);
    return { value, image: variant?.featured_image?.src || undefined };
  });
};

/** Reads the `flavor_title_color:<name>#<hex>` tag Shopify products carry. */
export const parseFlavorTag = (tags: ShopifyProduct['tags']): { name: string; color?: string } | null => {
  for (const tag of tagList(tags)) {
    const match = /^flavor_title_color:\s*(.+?)\s*(#[0-9a-f]{3,8})?\s*$/i.exec(tag);
    if (match) return { name: collapseSpaces(match[1]), color: match[2]?.toLowerCase() };
  }
  return null;
};

export const sizedImage = (src: string, width = 600) => {
  if (!src) return src;
  return `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
};

export const emptyCatalog = (): Catalog => ({
  'prophy-paste': [],
  'adult-prophy-paste': [],
  'varnish-sticks': [],
  'varnish-cups': [],
  flow: [],
});

export interface ComingSoonFlavor {
  /** Handle of the draft Shopify product. */
  handle: string;
  line: ProductLineId;
  name: string;
  color?: string;
}

const LINE_IDS = new Set<string>(PRODUCT_LINES.map(line => line.id));

/** Validates src/data/coming-soon.json; entries that do not fit are skipped. */
export const readComingSoon = (raw: unknown): ComingSoonFlavor[] => {
  const list = raw && typeof raw === 'object' ? (raw as { flavors?: unknown }).flavors : undefined;
  if (!Array.isArray(list)) return [];
  return list.flatMap(item => {
    if (!item || typeof item !== 'object') return [];
    const { handle, line, name, color } = item as Record<string, unknown>;
    if (typeof handle !== 'string' || !handle.trim()) return [];
    if (typeof line !== 'string' || !LINE_IDS.has(line)) return [];
    if (typeof name !== 'string' || !cleanFlavorName(name)) return [];
    return [
      {
        handle: handle.trim(),
        line: line as ProductLineId,
        name: cleanFlavorName(name),
        color: typeof color === 'string' && /^#[0-9a-f]{3,8}$/i.test(color) ? color.toLowerCase() : undefined,
      },
    ];
  });
};

// Owner (5 Oct): Flow stays hidden from offices until launch, so the Coming Soon list is off unless
// REACT_APP_SHOW_COMING_SOON=true. When the Flow products go Active they appear as live products by themselves.
export const SHOW_COMING_SOON = process.env.REACT_APP_SHOW_COMING_SOON === 'true';
export const COMING_SOON: ComingSoonFlavor[] = SHOW_COMING_SOON ? readComingSoon(comingSoonConfig) : [];

export const parseProducts = (
  products: ShopifyProduct[],
  comingSoon: ComingSoonFlavor[] = COMING_SOON,
): Catalog => {
  const catalog = emptyCatalog();
  const seen = new Set<string>();
  const planned = new Map(comingSoon.map(flavor => [flavor.handle, flavor]));
  const liveHandles = new Set<string>();

  for (const product of products || []) {
    if (!product || typeof product.title !== 'string') continue;
    if (product.handle) liveHandles.add(product.handle);
    // A product named in coming-soon.json is the flavor named there, however
    // its title and type end up set in Shopify.
    const plan = product.handle ? planned.get(product.handle) : undefined;
    const line = plan ? plan.line : classifyProduct(product);
    if (!line) continue;

    const tag = parseFlavorTag(product.tags);
    const productImage = product.images && product.images[0] && product.images[0].src;
    const productUrl = product.handle ? `${STORE_ORIGIN}/products/${product.handle}` : undefined;

    // One product per flavor (today's store), or one product with a Flavor
    // option listing several (how a new line such as Flow may be set up).
    const variants = plan ? [] : flavorOptionValues(product);
    const entries = plan
      ? [{ name: plan.name, tagSlug: '', color: tag?.color || plan.color, image: productImage, handle: product.handle }]
      : variants.length
        ? variants.map(v => ({
            name: cleanFlavorName(v.value),
            tagSlug: '',
            color: undefined,
            image: v.image || productImage,
            handle: undefined,
          }))
        : [
            {
              name: flavorNameFromTitle(product.title) || (tag ? cleanFlavorName(tag.name.replace(/^adult\s+/i, '')) : ''),
              tagSlug: tag ? slugify(tag.name) : '',
              color: tag?.color,
              image: productImage,
              handle: product.handle,
            },
          ];

    for (const entry of entries) {
      if (!entry.name) continue;
      const slug = slugify(entry.name);
      const id = `${line}:${slug}`;
      if (!slug || seen.has(id)) continue;
      seen.add(id);

      const art = flavorArt(line, slug, entry.tagSlug);
      catalog[line].push({
        id,
        line,
        name: entry.name,
        slug,
        color: entry.color,
        image: art || (entry.image ? sizedImage(entry.image) : undefined),
        productUrl,
        handle: entry.handle,
      });
    }
  }

  // Draft flavors not in the storefront yet, unless a live product already
  // sells the same flavor in the same line.
  for (const plan of comingSoon) {
    if (liveHandles.has(plan.handle)) continue;
    const slug = slugify(plan.name);
    const id = `${plan.line}:${slug}`;
    if (!slug || seen.has(id)) continue;
    seen.add(id);
    catalog[plan.line].push({
      id,
      line: plan.line,
      name: plan.name,
      slug,
      color: plan.color,
      image: flavorArt(plan.line, slug),
      handle: plan.handle,
      comingSoon: true,
    });
  }

  for (const line of Object.keys(catalog) as ProductLineId[]) {
    catalog[line].sort((a, b) => a.name.localeCompare(b.name));
  }
  return catalog;
};

/** True when every flavor of a line is still Coming Soon (and there is at least one). */
export const isLineComingSoon = (catalog: Catalog, line: ProductLineId): boolean => {
  const flavors = catalog[line] || [];
  return flavors.length > 0 && flavors.every(flavor => flavor.comingSoon);
};

export const snapshotCatalog = (): Catalog =>
  parseProducts((snapshot as { products: ShopifyProduct[] }).products);

/** At least one flavor the store sells today; Coming Soon entries alone do not count. */
export const isUsableCatalog = (catalog: Catalog | null | undefined): catalog is Catalog =>
  !!catalog &&
  PRODUCT_LINES.every(line => Array.isArray(catalog[line.id])) &&
  PRODUCT_LINES.some(line => catalog[line.id].some(flavor => flavor && !flavor.comingSoon));

/**
 * A storefront product cut down to the fields parseProducts reads, so the
 * cache stays small (no descriptions, prices or inventory).
 */
export const trimProduct = (product: ShopifyProduct): ShopifyProduct => {
  const image = product.images && product.images[0] && product.images[0].src;
  return {
    title: product.title,
    handle: product.handle,
    product_type: product.product_type,
    // Only the tags the parser reads: the flavor color and the Flow tag.
    tags: tagList(product.tags).filter(tag => /^flavor_title_color:/i.test(tag.trim()) || FLOW_TAG.test(tag.trim())),
    images: image ? [{ src: image }] : [],
    options: (product.options || []).map(option => ({ name: option.name, values: option.values })),
    variants: (product.variants || []).map(variant => ({
      option1: variant?.option1 ?? null,
      option2: variant?.option2 ?? null,
      option3: variant?.option3 ?? null,
      featured_image: variant?.featured_image?.src ? { src: variant.featured_image.src } : null,
    })),
  };
};

interface CacheEntry {
  savedAt: number;
  products: ShopifyProduct[];
}

interface CachedCatalog {
  savedAt: number;
  catalog: Catalog;
}

/** The cached products, parsed now with this build's rules and Coming Soon list. */
const readCache = (): CachedCatalog | null => {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as CacheEntry;
    if (!entry || typeof entry.savedAt !== 'number' || !Array.isArray(entry.products)) return null;
    const catalog = parseProducts(entry.products);
    return isUsableCatalog(catalog) ? { savedAt: entry.savedAt, catalog } : null;
  } catch {
    return null;
  }
};

const writeCache = (products: ShopifyProduct[]) => {
  try {
    const entry: CacheEntry = { savedAt: Date.now(), products: products.filter(Boolean).map(trimProduct) };
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
    OLD_CACHE_KEYS.forEach(key => window.localStorage.removeItem(key));
  } catch {
    // Storage can be full or blocked (private mode, third-party iframe). The app still works.
  }
};

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

const defaultFetch: FetchLike = (input, init) => fetch(input, init);

export const fetchProducts = async (fetchImpl: FetchLike = defaultFetch): Promise<ShopifyProduct[]> => {
  const all: ShopifyProduct[] = [];
  for (let page = 1; page <= MAX_PAGES; page++) {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
    const timer = controller ? setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS) : undefined;
    try {
      const response = await fetchImpl(`${PRODUCTS_URL}?limit=${PAGE_LIMIT}&page=${page}`, {
        signal: controller?.signal,
        credentials: 'omit',
      });
      if (!response.ok) throw new Error(`products.json returned ${response.status}`);
      const body = await response.json();
      const products: ShopifyProduct[] = Array.isArray(body?.products) ? body.products : [];
      all.push(...products);
      if (products.length < PAGE_LIMIT) break;
    } finally {
      if (timer) clearTimeout(timer);
    }
  }
  return all;
};

export interface CatalogResult {
  catalog: Catalog;
  source: CatalogSource;
}

/**
 * Fresh cache, then the live storefront, then a stale cache, then the
 * bundled snapshot. Never throws.
 */
export const loadCatalog = async (
  fetchImpl: FetchLike = defaultFetch,
  now: () => number = Date.now,
): Promise<CatalogResult> => {
  const cached = readCache();
  if (cached && now() - cached.savedAt < CACHE_TTL_MS) {
    return { catalog: cached.catalog, source: 'cache' };
  }
  try {
    const products = await fetchProducts(fetchImpl);
    const catalog = parseProducts(products);
    if (isUsableCatalog(catalog)) {
      writeCache(products);
      return { catalog, source: 'live' };
    }
  } catch {
    // Offline, blocked or timed out: fall through to the fallbacks.
  }
  if (cached) return { catalog: cached.catalog, source: 'cache' };
  return { catalog: snapshotCatalog(), source: 'snapshot' };
};
